import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Intent, MenuItem } from '@blueprintjs/core';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { ListingTable } from 'components/listing';
import { StatusBadge } from 'components/status';
import { ArrayToEllipsesString, Numeric } from 'components/formatting';
import { DateToLocal } from 'components/dates';
import { Breadcrumbs, Tabs } from 'components/navigation';
import { NotificationToaster } from 'components/notifications';
import { ShowHide } from 'components/layout';
import { Button } from 'components/buttons';
import { Dropdown } from 'components/dropdown';
import { PageRestricted } from 'pages/errors/page-restricted';
import { useUserHasAccess } from 'hooks/useUserHasAccess';
import { BlobStorageService, FileExtractService } from 'services';

import { FileExtractRequestModal } from './FileExtractRequestModal';
import { FileExtractPartsModal } from './FileExtractPartsModal';

const defaultArray = [];

export const fileExtractAction = "Account:Extract";

// A disabled type is never offered or searched, but stays resolvable so existing rows of it render.
export const fileExtractTypes = [
    { id: "DriverCard", name: "Driver Card", microserviceKey: "employee", enabled: true },
    { id: "VehicleUnit", name: "Vehicle Unit", microserviceKey: "asset", enabled: true }
];

const enabledFileExtractTypes = fileExtractTypes.filter((type) => type.enabled);

const scopeFilterName = "SCOPE";
export const mineScope = "Mine";
export const allScope = "All";

const inFlightStatuses = ["Queued", "Processing"];

const completedStatus = "Completed";

const badRequestCode = 400;

const fetchRequestCount = 500;
const defaultPageSize = 25;
const pollIntervalMs = 10000;

const dateFormat = "DD/MM/YYYY";
const dateTimeFormat = "DD/MM/YYYY HH:mm";

const noValue = "-";
const percentageMultiplier = 100;

const partialFailureMessage = "Some file extracts could not be loaded. The list may be incomplete.";
const downloadErrorMessage = "Could not download the file. Please try again.";

const noFilesMatchedMessage = "No files matched";

const scopeTabs = [
    { title: "My Extracts", scope: mineScope, path: "/file-extract" },
    { title: "All Extracts", scope: allScope, path: "/file-extract/all" }
];

const typeColumnWidth = 140;
const dateRangeColumnWidth = 200;
const requesterColumnWidth = 160;
const statusColumnWidth = 220;
const filesColumnWidth = 180;
const requestedColumnWidth = 160;
const actionsColumnWidth = 120;

function byCreatedDateDescending(first, second) {
    return moment(second.createdDate).valueOf() - moment(first.createdDate).valueOf();
}

function getFileTypeName(typeId) {
    const fileType = fileExtractTypes.find((type) => type.id === typeId);
    return fileType == null ? typeId : fileType.name;
}

function getFileTypeServiceKey(typeId) {
    const fileType = fileExtractTypes.find((type) => type.id === typeId);
    return fileType == null ? null : fileType.microserviceKey;
}

function getStatusDisplay(status) {
    switch (status) {
        case "Queued":
            return { label: "Queued", iconName: "time", intent: Intent.NONE, animated: false };
        case "Processing":
            return { label: "Processing", iconName: "refresh", intent: Intent.PRIMARY, animated: true };
        case "Completed":
            return { label: "Completed", iconName: "tick-circle", intent: Intent.SUCCESS, animated: false };
        case "Completed With Errors":
            return { label: "Completed With Errors", iconName: "warning-sign", intent: Intent.WARNING, animated: false };
        case "Failed":
            return { label: "Failed", iconName: "error", intent: Intent.DANGER, animated: false };
        case "Expired":
            return { label: "Expired", iconName: "disable", intent: Intent.NONE, animated: false };
        default:
            return { label: status ?? "Unknown", iconName: "help", intent: Intent.NONE, animated: false };
    }
}

// The download link is issued against the requester, so only their own parts can be taken.
function canDownloadPart(extract, part) {
    return extract.requestedByMe === true
        && part.status === completedStatus
        && getFileTypeServiceKey(extract.type) != null;
}

function getDownloadableParts(extract) {
    return (extract.parts ?? defaultArray).filter((part) => canDownloadPart(extract, part));
}

function getPartsModalTitle(extract) {
    if (extract == null) {
        return "";
    }

    const start = moment(extract.startDate).format(dateFormat);
    const end = moment(extract.endDate).format(dateFormat);

    return `${getFileTypeName(extract.type)} Extract - ${start} to ${end}`;
}

// The total is unknown until the snapshot, and a percentage only means something while it moves.
function renderFilesCell(extract) {
    if (extract.totalFiles == null) {
        return (<>{noValue}</>);
    }

    if (extract.totalFiles === 0) {
        return (<>{noFilesMatchedMessage}</>);
    }

    const percentage = Math.round((extract.extractedFiles / extract.totalFiles) * percentageMultiplier);

    return (
        <>
            <Numeric>{extract.extractedFiles}</Numeric>
            {" of "}
            <Numeric>{extract.totalFiles}</Numeric>
            <ShowHide
                evaluator={inFlightStatuses.includes(extract.status)}
                show={(
                    <span>{` (${percentage}%)`}</span>
                )}
            />
            <ShowHide
                evaluator={extract.skippedFiles > 0}
                show={(
                    <span className="bp6-text-muted">{` (${extract.skippedFiles} skipped)`}</span>
                )}
            />
        </>
    );
}

export function FileExtractListing(
    {
        breadcrumbs = defaultArray,
        scope = mineScope,
        types = enabledFileExtractTypes
    }
) {

    const userHasAccess = useUserHasAccess();
    const hasAccess = userHasAccess(fileExtractAction);

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [extracts, setExtracts] = useState(defaultArray);
    const [isLoading, setIsLoading] = useState(true);
    const [showRequestModal, setShowRequestModal] = useState(false);

    const [downloadingPartIds, setDownloadingPartIds] = useState(defaultArray);

    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(defaultPageSize);

    // Only the id: polling replaces every row each cycle, so the extract is read back out of the list.
    const [partsModalExtractId, setPartsModalExtractId] = useState(null);

    // A service that is down stays down across every poll, so the warning is raised once per visit.
    const searchFailureReported = useRef(false);

    // Stamped per read so a slow read cannot overwrite a newer one and step the listing backwards.
    const fetchGeneration = useRef(0);

    // The top bar resets the width on every route change, so this is keyed on scope to survive a tab switch.
    useEffect(() => {
        dispatch({ type: 'SITE_FULL_WIDTH' });
    }, [dispatch, scope]);

    const extractServiceKeys = [...new Set(types.map((type) => type.microserviceKey))];

    function fetchExtracts() {
        fetchGeneration.current = fetchGeneration.current + 1;
        const generation = fetchGeneration.current;

        const searchRequest = {
            requestCount: fetchRequestCount,
            pageNumber: 1,
            searchTerm: null,
            sortField: "createdDate",
            sortDirection: "D",
            filters: [{ key: scopeFilterName, value: scope }]
        };

        // Resolved independently so one service being down still leaves the other's extracts on screen.
        const requests = extractServiceKeys.map((serviceKey) => {
            return FileExtractService.searchExtracts(serviceKey, searchRequest).then(
                (response) => {
                    return { succeeded: true, rows: response?.data ?? defaultArray };
                },
                () => {
                    return { succeeded: false, rows: defaultArray };
                }
            );
        });

        Promise.all(requests).then((results) => {
            if (generation !== fetchGeneration.current) {
                return;
            }

            const merged = results.reduce((accumulator, result) => accumulator.concat(result.rows), []);
            merged.sort(byCreatedDateDescending);

            setExtracts(merged);
            setIsLoading(false);

            if (results.some((result) => !result.succeeded) && !searchFailureReported.current) {
                searchFailureReported.current = true;
                NotificationToaster.show(Intent.DANGER, partialFailureMessage);
            }
        });
    }

    useEffect(() => {
        if (!hasAccess) {
            setIsLoading(false);
            return;
        }

        // The rows on screen belong to the previous scope, so they are dropped while the new one loads.
        setExtracts(defaultArray);
        setPageNumber(1);
        setIsLoading(true);
        fetchExtracts();
    }, [hasAccess, scope]);

    const partsModalExtract = partsModalExtractId == null
        ? null
        : extracts.find((extract) => extract.id === partsModalExtractId) ?? null;

    // The extract has gone from the list, so there is nothing left to pick from.
    useEffect(() => {
        if (partsModalExtractId != null && partsModalExtract == null) {
            setPartsModalExtractId(null);
        }
    }, [partsModalExtractId, partsModalExtract]);

    // Only an in-flight extract can change, so polling stops once every row is terminal.
    const anyInFlight = extracts.some((extract) => inFlightStatuses.includes(extract.status));

    useEffect(() => {
        if (!hasAccess || !anyInFlight) {
            return undefined;
        }

        const intervalId = setInterval(fetchExtracts, pollIntervalMs);
        return () => clearInterval(intervalId);
    }, [hasAccess, anyInFlight, scope]);

    function onNewExtractClick() {
        setShowRequestModal(true);
    }

    function onCloseRequestModal() {
        setShowRequestModal(false);
    }

    // The new extract only appears once the lists are read again.
    function onRequested() {
        setShowRequestModal(false);
        fetchExtracts();
    }

    function downloadPart(descriptor) {
        return BlobStorageService.downloadFile(
            descriptor.storageName,
            descriptor.containerName,
            descriptor.fileName,
            descriptor.token,
            descriptor.downloadName
        );
    }

    // A refused token means the row is out of date, so the lists are re-read to catch it up.
    function reportTokenFailure(error) {
        if (error?.response?.status === badRequestCode) {
            NotificationToaster.show(Intent.WARNING, error.response.data);
            fetchExtracts();
            return;
        }

        NotificationToaster.show(Intent.DANGER, downloadErrorMessage);
    }

    // The token is a short-lived credential, so it is handed straight to the download and never held.
    async function downloadSinglePart(extract, part) {
        setDownloadingPartIds((current) => [...current, part.id]);

        try {
            const descriptor = await FileExtractService.getPartDownloadToken(
                getFileTypeServiceKey(extract.type),
                extract.id,
                part.id
            );

            try {
                await downloadPart(descriptor);
            } catch {
                NotificationToaster.show(Intent.DANGER, downloadErrorMessage);
                return false;
            }

            return true;
        } catch (error) {
            reportTokenFailure(error);
            return false;
        } finally {
            // Released by id, so anything else still coming down is untouched.
            setDownloadingPartIds((current) => current.filter((id) => id !== part.id));
        }
    }

    // A failure is already reported, so the run stops instead of repeating it for every part left.
    async function downloadAllParts(extract) {
        const parts = getDownloadableParts(extract);

        for (const part of parts) {
            const succeeded = await downloadSinglePart(extract, part);

            if (!succeeded) {
                return;
            }
        }
    }

    function onOpenParts(extract) {
        setPartsModalExtractId(extract.id);
    }

    function onClosePartsModal() {
        setPartsModalExtractId(null);
    }

    function onDownloadModalPart(part) {
        downloadSinglePart(partsModalExtract, part);
    }

    // The control is chosen by how many parts exist, not how many are ready; the snapshot makes them all.
    function renderRowActions(extract) {
        const parts = extract.parts ?? defaultArray;
        const downloadableParts = getDownloadableParts(extract);

        if (parts.length === 0) {
            return (
                <Button
                    id={`file-extract-download-${extract.id}`}
                    text="Download"
                    intent="primary"
                    large={false}
                    disabled={true}
                />
            );
        }

        if (parts.length === 1) {
            const onlyPart = parts[0];

            return (
                <Button
                    id={`file-extract-download-${extract.id}`}
                    text="Download"
                    intent="primary"
                    large={false}
                    disabled={downloadingPartIds.includes(onlyPart.id) || !canDownloadPart(extract, onlyPart)}
                    onClick={() => downloadSinglePart(extract, onlyPart)}
                />
            );
        }

        // A half-built set would look complete, so "All" waits until no further part can appear.
        const canDownloadEverything = !inFlightStatuses.includes(extract.status) && downloadableParts.length > 0;

        // Overlapping a hand-picked part of this same extract would fetch it twice: one file twice,
        // one intent audited twice. Another extract's parts are different files and cannot collide.
        const thisExtractDownloading = parts.some((part) => downloadingPartIds.includes(part.id));

        return (
            <Dropdown
                id={`file-extract-download-${extract.id}`}
                text="Download"
                intent="primary"
                large={false}
                items={[
                    <MenuItem
                        key={`file-extract-download-all-${extract.id}`}
                        id={`file-extract-download-all-${extract.id}`}
                        text="Download All"
                        disabled={!canDownloadEverything || thisExtractDownloading}
                        onClick={() => downloadAllParts(extract)}
                    />,
                    <MenuItem
                        key={`file-extract-download-parts-${extract.id}`}
                        id={`file-extract-download-parts-${extract.id}`}
                        text="Download Separately"
                        onClick={() => onOpenParts(extract)}
                    />
                ]}
            />
        );
    }

    const showRequester = scope === allScope;
    const selectedTabIndex = scopeTabs.findIndex((tab) => tab.scope === scope);

    const tabs = scopeTabs.map((tab, index) => {
        return { title: tab.title, selected: index === selectedTabIndex };
    });

    function onTabClick(index) {
        if (index !== selectedTabIndex) {
            navigate(scopeTabs[index].path);
        }
    }

    // The table owns which page is shown, so its choice is mirrored into the state the slice
    // below reads. Anything that set these independently would put the two out of step.
    function onPagingChange(newPageNumber, newPageSize) {
        setPageNumber(newPageNumber);
        setPageSize(newPageSize);
    }

    // Declared in pairs so the optional requester column cannot put headers and widths out of step.
    // Locations is left unset, so the table's auto-layout gives it whatever the pinned ones leave.
    const columns = [
        { header: "Type", width: typeColumnWidth },
        { header: "Date range", width: dateRangeColumnWidth },
        { header: "Locations", width: null }
    ];

    if (showRequester) {
        columns.push({ header: "Requested by", width: requesterColumnWidth });
    }

    columns.push(
        { header: "Status", width: statusColumnWidth },
        { header: "Files", width: filesColumnWidth },
        { header: "Requested", width: requestedColumnWidth },
        { header: "", width: actionsColumnWidth }
    );

    const headers = columns.map((column) => column.header);
    const columnWidths = columns.map((column) => column.width);

    // Polling changes the row count under the user, and ListingTable re-seats itself on its own
    // startPage whenever that count moves. Driving startPage from here keeps the highlighted page
    // and the rendered slice in step, and clamping keeps the page in range when rows go away.
    const totalPages = Math.max(1, Math.ceil(extracts.length / pageSize));
    const activePage = Math.min(pageNumber, totalPages);

    const pageStart = (activePage - 1) * pageSize;
    const pageExtracts = extracts.slice(pageStart, pageStart + pageSize);

    const rowData = pageExtracts.map((extract) => {
        const statusDisplay = getStatusDisplay(extract.status);
        const locationNames = (extract.locations ?? defaultArray).map((location) => location.name);

        const rowContent = [
            getFileTypeName(extract.type),
            (
                <span key={`file-extract-dates-${extract.id}`}>
                    <DateToLocal format={dateFormat}>{extract.startDate}</DateToLocal>
                    {" to "}
                    <DateToLocal format={dateFormat}>{extract.endDate}</DateToLocal>
                </span>
            ),
            (
                <ArrayToEllipsesString key={`file-extract-locations-${extract.id}`}>{locationNames}</ArrayToEllipsesString>
            )
        ];

        if (showRequester) {
            rowContent.push(extract.requestedBy ?? noValue);
        }

        rowContent.push(
            (
                <StatusBadge
                    key={`file-extract-status-${extract.id}`}
                    text={statusDisplay.label}
                    iconName={statusDisplay.iconName}
                    intent={statusDisplay.intent}
                    animated={statusDisplay.animated}
                />
            ),
            (
                <span key={`file-extract-files-${extract.id}`}>{renderFilesCell(extract)}</span>
            ),
            (
                <DateToLocal key={`file-extract-created-${extract.id}`} format={dateTimeFormat}>{extract.createdDate}</DateToLocal>
            ),
            renderRowActions(extract)
        );

        return rowContent;
    });

    return (
        <ShowHide
            evaluator={hasAccess}
            show={(
                <div className="row">
                    <Breadcrumbs items={breadcrumbs} />

                    <h1>File Extract</h1>

                    <div className="button-row button-row-stacked-mobile spacer-bottom">
                        <Button
                            id="new-file-extract"
                            intent="primary"
                            text="New Extract"
                            onClick={onNewExtractClick}
                        />
                    </div>

                    <div className="spacer-bottom">
                        <Tabs tabs={tabs} onClick={onTabClick} />
                    </div>

                    <ListingTable
                        id="file-extract-listing-table"
                        loadingData={isLoading}
                        headers={headers}
                        data={rowData}
                        columnWidths={columnWidths}
                        totalRecordCount={extracts.length}
                        showPerPage={true}
                        pageSize={defaultPageSize}
                        startPage={activePage}
                        onPagingChange={onPagingChange}
                        rowClickEnabled={false}
                        noDataHeading="No extracts"
                        noDataMessage="There are no file extracts to display."
                    />

                    <ShowHide
                        evaluator={showRequestModal}
                        show={(
                            <FileExtractRequestModal
                                showModal={showRequestModal}
                                onSave={onRequested}
                                onCloseModal={onCloseRequestModal}
                                types={types}
                            />
                        )}
                    />

                    <ShowHide
                        evaluator={partsModalExtract != null}
                        show={(
                            <FileExtractPartsModal
                                showModal={partsModalExtract != null}
                                title={getPartsModalTitle(partsModalExtract)}
                                parts={partsModalExtract?.parts}
                                downloadingPartIds={downloadingPartIds}
                                canDownloadPart={(part) => canDownloadPart(partsModalExtract, part)}
                                getStatusDisplay={getStatusDisplay}
                                onDownloadPart={onDownloadModalPart}
                                onCloseModal={onClosePartsModal}
                            />
                        )}
                    />
                </div>
            )}
            hide={(
                <PageRestricted />
            )}
        />
    );
}

FileExtractListing.propTypes = {
    breadcrumbs: PropTypes.array,
    scope: PropTypes.string,
    types: PropTypes.array
};
